import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid2,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import {
    dismissUpgradeOffer,
    sendBulkUpgradeOffer,
    sendUpgradeReminder,
    useGetUpgradeOffers,
} from '@openapi/upgrade-offers/upgrade-offers';
import useNotify from '../../hooks/useNotify';
import useSorting from '../../hooks/useSorting';
import { TABLE_ROW_COUNT_DEFAULT } from '../../helpers/constants';
import { formatDateTimeInVenueTz } from '../../helpers/formatDateTime';
import PageWithTitle from '../../components/atom/PageWithTitle';
import SearchInput from '../../components/atom/SearchInput';
import UpgradeOffersTable from './components/UpgradeOffersTable';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

const FILTER_CTL = { '& .MuiOutlinedInput-root': { height: 40 } };
const FILTER_LBL = { mb: 0.5, textAlign: 'left' };

const UpgradesPage = () => {
    const { t } = useTranslation();
    const { notifyError, notifySuccess, notifyInfo } = useNotify();
    const [{ page, limit }, setParams] = useState({ page: 0, limit: TABLE_ROW_COUNT_DEFAULT });
    const [searchParams] = useSearchParams();
    // Seed the show/timeslot from the URL so the upgrade-eligible email deep
    // link (/upgrades?showId=&timeslotId=) lands on the right selection; the
    // auto-select effect below keeps these when present in filterOptions.
    const [selectedShowId, setSelectedShowId] = useState(searchParams.get('showId') || '');
    const [selectedTimeslotId, setSelectedTimeslotId] = useState(
        searchParams.get('timeslotId') || '',
    );
    const [search, setSearch] = useState('');
    // Cache the last filterOptions response. The QueryClient is configured
    // staleTime:0 / gcTime:0 / keepPreviousData:false, so every queryKey
    // change drops offersData to undefined for the duration of the refetch.
    // Caching keeps the dropdown items stable across selection changes -
    // otherwise MUI Select renders with a controlled value but no matching
    // MenuItems and falls into an internal setState loop.
    const [cachedFilterOptions, setCachedFilterOptions] = useState(null);
    const {
        sort: columnSortField,
        sortDir: columnSortDir,
        setSorting: setColumnSorting,
    } = useSorting(['', 'ASC']);
    const [onlyEligible, setOnlyEligible] = useState(true);
    const [onlyNeverNotified, setOnlyNeverNotified] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [sendingOfferIds, setSendingOfferIds] = useState([]);
    const [sendingReminderIds, setSendingReminderIds] = useState([]);
    const [dismissingIds, setDismissingIds] = useState([]);
    const [sendingBulkOffer, setSendingBulkOffer] = useState(false);

    const resetSelectionAndPage = () => {
        setSelectedIds([]);
        setParams((prev) => ({ ...prev, page: 0 }));
    };

    const queryParams = {
        showId: selectedShowId ? Number(selectedShowId) : undefined,
        timeslotId: selectedTimeslotId ? Number(selectedTimeslotId) : undefined,
        search: search || undefined,
        page,
        pageSize: limit,
        sort: columnSortField || undefined,
        sortDir: columnSortField ? columnSortDir : undefined,
        onlyEligible: onlyEligible || undefined,
        onlyNeverNotified: onlyNeverNotified || undefined,
    };

    const {
        data: offersData,
        isLoading: loadingOffers,
        isFetching,
        refetch: refetchOffers,
    } = useGetUpgradeOffers(queryParams);

    const offers = offersData?.offers || [];
    const total = offersData?.total || 0;
    const shows = cachedFilterOptions?.shows || [];
    const timeslotsByShowId = cachedFilterOptions?.timeslotsByShowId || {};
    const timeslots = timeslotsByShowId[String(selectedShowId)] || [];
    const isOffersInitialLoading = !offersData && (loadingOffers || isFetching);
    const visibleSelectedIds = selectedIds.filter((id) =>
        offers.some((o) => o.bookingId === id && o.status === 'eligible'),
    );

    // Refresh the cached filter options whenever a fresh response arrives.
    useEffect(() => {
        if (offersData?.filterOptions) {
            setCachedFilterOptions(offersData.filterOptions);
        }
    }, [offersData?.filterOptions]);

    // Auto-select sane defaults once filter options are available. We read
    // from cachedFilterOptions (not the live offersData) so the effect
    // doesn't fire during refetch-driven loading states - see comment on
    // cachedFilterOptions for why that matters.
    useEffect(() => {
        if (!cachedFilterOptions) return;
        const fShows = cachedFilterOptions.shows || [];
        const fTimeslotsByShowId = cachedFilterOptions.timeslotsByShowId || {};
        const showStillAvailable = fShows.some((s) => String(s.id) === String(selectedShowId));
        const nextShowId =
            fShows.length === 0 ? '' : showStillAvailable ? selectedShowId : String(fShows[0].id);
        const nextTimeslots = fTimeslotsByShowId[String(nextShowId)] || [];
        const timeslotStillAvailable = nextTimeslots.some(
            (ts) => String(ts.id) === String(selectedTimeslotId),
        );
        const nextTimeslotId = !nextTimeslots.length
            ? ''
            : showStillAvailable && timeslotStillAvailable
              ? selectedTimeslotId
              : String(nextTimeslots[0].id);
        if (nextShowId !== selectedShowId || nextTimeslotId !== selectedTimeslotId) {
            setSelectedShowId(nextShowId);
            setSelectedTimeslotId(nextTimeslotId);
            setSelectedIds([]);
            setParams((prev) => ({ ...prev, page: 0 }));
        }
    }, [selectedShowId, selectedTimeslotId, cachedFilterOptions]);

    useEffect(() => {
        if (page > 0 && offers.length === 0) {
            setParams((prev) => ({ ...prev, page: Math.max(0, prev.page - 1) }));
        }
    }, [page, offers.length]);

    const handleSendOffer = async (bookingId) => {
        setSendingOfferIds((current) => [...current, bookingId]);
        try {
            const body = await sendBulkUpgradeOffer({ bookingIds: [bookingId] });
            if ((body.sentCount || 0) > 0) {
                notifySuccess(t('upgrades.notify.offerSent'));
            } else {
                const status = body.results?.[0]?.status;
                if (status === 'failed') {
                    notifyError(t('upgrades.notify.bulkFailed', { count: 1 }));
                } else if (typeof status === 'string' && status.startsWith('skipped')) {
                    notifyInfo(t('upgrades.notify.bulkSkipped', { count: 1 }));
                } else {
                    notifyError(t('upgrades.notify.bulkFailed', { count: 1 }));
                }
            }
            await refetchOffers();
        } catch (error) {
            notifyError(error);
        } finally {
            setSendingOfferIds((current) => current.filter((id) => id !== bookingId));
        }
    };

    const handleSendReminder = async (offerId) => {
        setSendingReminderIds((current) => [...current, offerId]);
        try {
            await sendUpgradeReminder(offerId);
            notifySuccess(t('upgrades.notify.reminderSent'));
            await refetchOffers();
        } catch (error) {
            notifyError(error);
        } finally {
            setSendingReminderIds((current) => current.filter((id) => id !== offerId));
        }
    };

    const handleDismiss = async (row) => {
        const isEligible = row.status === 'eligible';
        const dismissKey = isEligible ? row.bookingId : row.id;
        setDismissingIds((current) => [...current, dismissKey]);
        try {
            await dismissUpgradeOffer(
                isEligible
                    ? {
                          bookingId: row.bookingId,
                          showId: Number(selectedShowId),
                          timeslotId: Number(selectedTimeslotId),
                      }
                    : { upgradeOfferId: row.id },
            );
            notifySuccess(t('upgrades.notify.dismissed'));
            await refetchOffers();
            setSelectedIds((current) => current.filter((id) => id !== row.bookingId));
        } catch (error) {
            notifyError(error);
        } finally {
            setDismissingIds((current) => current.filter((id) => id !== dismissKey));
        }
    };

    const handleBulkSendOffer = async () => {
        if (visibleSelectedIds.length === 0 || sendingBulkOffer) return;
        setSendingBulkOffer(true);
        try {
            const body = await sendBulkUpgradeOffer({ bookingIds: visibleSelectedIds });
            const sent = body.sentCount || 0;
            const failed = body.failedCount || 0;
            const details = [
                body.skippedCount && t('upgrades.notify.bulkSkipped', { count: body.skippedCount }),
                failed && t('upgrades.notify.bulkFailed', { count: failed }),
            ]
                .filter(Boolean)
                .join(', ');
            if (sent > 0) {
                notifySuccess(t('upgrades.notify.bulkSent', { count: sent }));
                if (details) notifyInfo(details);
            } else if (details) {
                (failed > 0 ? notifyError : notifyInfo)(details);
            }
            await refetchOffers();
            const sentIds = new Set(
                (body.results || []).filter((e) => e.status === 'sent').map((e) => e.bookingId),
            );
            if (sentIds.size > 0) {
                setSelectedIds((current) => current.filter((id) => !sentIds.has(id)));
            }
        } catch (error) {
            notifyError(error);
        } finally {
            setSendingBulkOffer(false);
        }
    };

    const handleColumnSort = (nextSortField) => {
        setColumnSorting(nextSortField);
        resetSelectionAndPage();
    };

    return (
        <PageWithTitle
            title={t('upgrades.pageTitle')}
            headline={[
                <Button
                    key="send-bulk-offer"
                    variant="contained"
                    color="secondary"
                    startIcon={<SendIcon />}
                    disabled={visibleSelectedIds.length === 0 || sendingBulkOffer}
                    onClick={handleBulkSendOffer}
                    sx={{ textTransform: 'none' }}
                >
                    {t('upgrades.actions.sendBulkOffer')}
                </Button>,
            ]}
        >
            <Grid2 size={{ xs: 12 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                        <Grid2 container spacing={2} sx={{ alignItems: 'flex-end' }}>
                            <Grid2 size={{ xs: 12, md: 3 }}>
                                <InputLabel sx={FILTER_LBL}>
                                    {t('upgrades.filters.show')}
                                </InputLabel>
                                <FormControl
                                    fullWidth
                                    size="small"
                                    hiddenLabel
                                    disabled={shows.length === 0}
                                    sx={FILTER_CTL}
                                >
                                    <Select
                                        value={selectedShowId}
                                        onChange={(e) => {
                                            setSelectedShowId(String(e.target.value));
                                            setSelectedTimeslotId('');
                                            resetSelectionAndPage();
                                        }}
                                    >
                                        {shows.map((s) => (
                                            <MenuItem key={s.id} value={String(s.id)}>
                                                {s.title}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 3 }}>
                                <InputLabel sx={FILTER_LBL}>
                                    {t('upgrades.filters.timeslot')}
                                </InputLabel>
                                <FormControl
                                    fullWidth
                                    size="small"
                                    hiddenLabel
                                    disabled={!selectedShowId || timeslots.length === 0}
                                    sx={FILTER_CTL}
                                >
                                    <Select
                                        value={selectedTimeslotId}
                                        onChange={(e) => {
                                            setSelectedTimeslotId(String(e.target.value));
                                            resetSelectionAndPage();
                                        }}
                                    >
                                        {timeslots.map((ts) => (
                                            <MenuItem key={ts.id} value={String(ts.id)}>
                                                {formatDateTimeInVenueTz(ts.startTime)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }}>
                                <InputLabel sx={FILTER_LBL}>
                                    {t('upgrades.filters.search')}
                                </InputLabel>
                                <Box sx={FILTER_CTL}>
                                    <SearchInput
                                        fullWidth
                                        variant="outlined"
                                        search={search}
                                        setSearch={setSearch}
                                        setParams={setParams}
                                        limit={limit}
                                        placeholder={t('upgrades.filters.searchPlaceholder')}
                                    />
                                </Box>
                            </Grid2>
                        </Grid2>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={{ xs: 1, sm: 2 }}
                            flexWrap="wrap"
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={onlyEligible}
                                        onChange={(e) => {
                                            setOnlyEligible(e.target.checked);
                                            resetSelectionAndPage();
                                        }}
                                    />
                                }
                                label={t('upgrades.filters.eligibleOnly')}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={onlyNeverNotified}
                                        onChange={(e) => {
                                            setOnlyNeverNotified(e.target.checked);
                                            resetSelectionAndPage();
                                        }}
                                    />
                                }
                                label={t('upgrades.filters.neverNotifiedOnly')}
                            />
                        </Stack>
                    </Stack>
                </Paper>
            </Grid2>

            {sendingBulkOffer && (
                <Grid2 size={{ xs: 12 }}>
                    <Box sx={{ mt: 0, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {t('upgrades.sendingOffers')}
                        </Typography>
                        <LinearProgress />
                    </Box>
                </Grid2>
            )}

            {!selectedShowId || !selectedTimeslotId ? (
                <Grid2 size={{ xs: 12 }}>
                    <Alert severity="info">{t('upgrades.selectShowAndTimeslot')}</Alert>
                </Grid2>
            ) : (
                <UpgradeOffersTable
                    rows={offers}
                    total={total}
                    page={page}
                    limit={limit}
                    setParams={setParams}
                    loading={isOffersInitialLoading}
                    selectedIds={selectedIds}
                    setSelectedIds={setSelectedIds}
                    onSendOffer={handleSendOffer}
                    onSendReminder={handleSendReminder}
                    onDismiss={handleDismiss}
                    sendingOfferIds={sendingOfferIds}
                    sendingReminderIds={sendingReminderIds}
                    dismissingIds={dismissingIds}
                    sort={columnSortField}
                    sortDir={columnSortDir}
                    onSort={handleColumnSort}
                    hasActiveFilters={!!search.trim() || !onlyEligible || onlyNeverNotified}
                />
            )}
        </PageWithTitle>
    );
};

export default UpgradesPage;
